import React from 'react';
import dayjs from 'dayjs';
import { Box, Paper, Typography, Chip, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';

/**
 * Returns a colour for a task's urgency based on its do_date.
 * @param {string} doDate - YYYY-MM-DD
 */
const urgencyColor = (doDate) => {
  if (!doDate) return 'default';
  const date = dayjs(doDate);
  if (date.isBefore(dayjs(), 'day')) return 'error'; // overdue
  if (date.isSame(dayjs(), 'day')) return 'warning'; // due today
  return 'default';
};

/**
 * "Do today" commitment blocks: open tasks due today or overdue, sorted by
 * priority (line_up, supplied pre-sorted by the API).
 *
 * @param {object} props
 * @param {Array<object>} props.tasks - Tasks from the /calendar do_today payload.
 */
const DoTodayPanel = ({ tasks = [] }) => {
  const navigate = useNavigate();

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
        Do today
      </Typography>

      {tasks.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Nothing due today.
        </Typography>
      )}

      <Stack spacing={1}>
        {tasks.map((task) => (
          <Box
            key={task.id}
            onClick={() => navigate(`/tasks/${task.id}`)}
            sx={{
              p: 1,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              borderLeft: 4,
              borderLeftColor: `${urgencyColor(task.do_date) === 'default' ? 'primary' : urgencyColor(task.do_date)}.main`,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {task.name}
              </Typography>
              <Chip
                size="small"
                color={urgencyColor(task.do_date)}
                label={task.do_date ? dayjs(task.do_date).format('MMM D') : '—'}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {task.track_name || ''} · {task.status} · P{task.line_up}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

export default DoTodayPanel;
